<template>
  <div v-bind:key="refreshKey" class="activity-table-wrapper">
   <b-overlay :show="loading" rounded="sm" opacity="0.5" spinner-variant="primary">

    <b-table responsive
             v-handy-scroll
             v-bind:items="userActivity"
             v-bind:fields="fields"
             no-local-sorting
             v-bind:sort-by.sync="sort.field"
             v-bind:sort-desc.sync="sortIsDesc"
             hover
             @sort-changed="onSortChanged"
             @row-clicked="onRowClicked"
             class="activity-table"
             style="white-space: nowrap;"
             thead-class="activity-table__head"
             tbody-class="activity-table__body"
    >

      <template #cell(registrationTime)="data">
        <div class="activity-cell-time">
          <div class="activity-cell-time__date">{{ formatDate(data.value) }}</div>
          <div class="activity-cell-time__hour">{{ formatTime(data.value) }}</div>
        </div>
      </template>

      <template #cell(executor)="data">
        <div class="activity-cell-user" v-if="data.value">
          <div class="activity-cell-user__avatar"
               :style="{ background: avatarColor(data.value.name) }">
            {{ initials(data.value.name) }}
          </div>
          <div class="activity-cell-user__info">
            <div class="activity-cell-user__name">{{ data.value.name || '—' }}</div>
            <div class="activity-cell-user__type" v-if="data.value.type">{{ data.value.type }}</div>
          </div>
        </div>
        <span class="text-muted" v-else>—</span>
      </template>

      <template #cell(module)="data">
        <b-badge class="activity-module-badge"
                 :style="{ background: moduleColor(data.value), color: '#fff' }">
          <i :class="moduleIcon(data.value)" class="mr-25"></i>
          {{ moduleLabel(data.value) }}
        </b-badge>
      </template>

      <template #cell(action)="data">
        <span class="activity-action-chip"
              :class="'activity-action-chip--' + actionVariant(data.value)">
          <i :class="actionIcon(data.value)" class="mr-25"></i>
          {{ actionLabel(data.value) }}
        </span>
      </template>

      <template #cell(description)="data">
        <div class="activity-cell-description" :title="data.value">
          {{ data.value || '—' }}
        </div>
      </template>

      <template #empty>
        <div class="activity-empty">
          <i class="fas fa-inbox activity-empty__icon"></i>
          <div class="activity-empty__title">{{ $t('globalMessages.noData') }}</div>
          <div class="activity-empty__hint">{{ $t('userActivity.emptyHint') }}</div>
        </div>
      </template>

    </b-table>

    <div class="activity-footer">
      <div class="activity-footer__pager">
        <b-pagination
            v-model="pagination.page"
            v-bind:total-rows="pagination.total"
            v-bind:per-page="pagination.size"
            @page-click="onPageClick"
            align="center"
            size="sm"
            class="mb-0"
        ></b-pagination>
      </div>
      <div class="activity-footer__size">
        <b-select style="width: 75px" size="sm" v-model="pagination.size" @change="refresh">
          <b-select-option v-bind:value="25">25</b-select-option>
          <b-select-option v-bind:value="50">50</b-select-option>
          <b-select-option v-bind:value="100">100</b-select-option>
        </b-select>
        <span class="activity-footer__summary">
          {{ pagination.total }} {{ $t('columns.counterNames.userActivity') }}
        </span>
      </div>
    </div>
   </b-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';

const MODULE_META = {
  USER:                { color: '#007BFF', icon: 'fas fa-user' },
  CLIENT:              { color: '#28c76f', icon: 'fas fa-user-tie' },
  CLIENT_NOTE:         { color: '#28c76f', icon: 'fas fa-sticky-note' },
  CLIENT_STATUS:       { color: '#28c76f', icon: 'fas fa-flag' },
  AFFILIATE:           { color: '#00cfe8', icon: 'fas fa-handshake' },
  AFFILIATE_TOKEN:     { color: '#00cfe8', icon: 'fas fa-key' },
  AFFILIATE_DASHBOARD: { color: '#00cfe8', icon: 'fas fa-chart-line' },
  MONETARY_TRANSACTION:{ color: '#ff9f43', icon: 'fas fa-money-bill-wave' },
  ORGANIZATION:        { color: '#6e6b7b', icon: 'fas fa-building' },
  BUSINESS_UNIT:       { color: '#6e6b7b', icon: 'fas fa-sitemap' },
  ROLE:                { color: '#ea5455', icon: 'fas fa-user-shield' },
  PERMISSION:          { color: '#ea5455', icon: 'fas fa-lock' },
  KYC:                 { color: '#ff6f91', icon: 'fas fa-id-card' },
  WHITELIST:           { color: '#ea5455', icon: 'fas fa-shield-alt' },
  TICKET:              { color: '#007BFF', icon: 'fas fa-ticket-alt' },
  LEAD:                { color: '#28c76f', icon: 'fas fa-user-plus' },
  DOCUMENT:            { color: '#6e6b7b', icon: 'fas fa-file-alt' },
  CALL:                { color: '#7367f0', icon: 'fas fa-phone' },
};

const ACTION_META = {
  CREATE:                { variant: 'success', icon: 'fas fa-plus-circle',    label: 'Create'            },
  UPDATE:                { variant: 'info',    icon: 'fas fa-pen',            label: 'Update'            },
  UPDATE_PART:           { variant: 'info',    icon: 'fas fa-edit',           label: 'Update part'       },
  DELETE:                { variant: 'danger',  icon: 'fas fa-trash',          label: 'Delete'            },
  DUPLICATE:             { variant: 'info',    icon: 'fas fa-copy',           label: 'Duplicate'         },
  RETRIEVE:              { variant: 'neutral', icon: 'fas fa-eye',            label: 'Retrieve'          },
  LOGIN:                 { variant: 'success', icon: 'fas fa-sign-in-alt',    label: 'Login'             },
  'LOGIN-FAILED':        { variant: 'danger',  icon: 'fas fa-user-slash',     label: 'Login failed'      },
  'RENEW PASSWORD':      { variant: 'warning', icon: 'fas fa-key',            label: 'Renew password'    },
  MONETARY_TRANSACTION:  { variant: 'warning', icon: 'fas fa-exchange-alt',   label: 'Transaction'       },
  APPROVE:               { variant: 'success', icon: 'fas fa-check',          label: 'Approve'           },
  DECLINE:               { variant: 'danger',  icon: 'fas fa-times',          label: 'Decline'           },
  STATUS_CHANGE:         { variant: 'info',    icon: 'fas fa-random',         label: 'Status change'     },
  COMMENT:               { variant: 'info',    icon: 'fas fa-comment',        label: 'Comment'           },
  AFFILIATE_REGISTRATION:{ variant: 'success', icon: 'fas fa-user-plus',      label: 'Affiliate reg.'    },
  HIERARCHY:             { variant: 'neutral', icon: 'fas fa-sitemap',        label: 'Hierarchy'         },
  CALL:                  { variant: 'success', icon: 'fas fa-phone',          label: 'Call'              },
  CALL_FAILED:           { variant: 'danger',  icon: 'fas fa-phone-slash',    label: 'Call failed'       },
};

export default {
  name: 'UserActivityTable',
  components: {},
  mixins: [mixinBase, mixinTable, mixinNotifications],
  data: () => ({
    userActivity: [],
    comment: '',
    isUserActivityModalVisible: false,
    title: '',
    showModal: false,
  }),
  props: {
    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    this.refresh();
  },
  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById']),

    fields() {
      let $this = this;
      let fields = (this.columns || []).map(column => {
        let label = column.key === 'clientId' ? $this.$t('columns.client') : $this.$t(`columns.${column.key}`);
        return {
          key: column.key,
          label: label,
          sortable: column.key === 'registrationTime',
          active: column.enable,
          thClass: `activity-th activity-th--${column.key}`,
          tdClass: `activity-td activity-td--${column.key}`,
        };
      });

      fields = fields.filter(field => !field.key.startsWith('id'));
      return fields.filter(f => f.active === true);
    },
  },
  methods: {
    ...mapActions('tracker', ['getAllTracker']),

    async refresh() {
      let $this = this;
      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'registrationTime';
      }
      const result = await this.getAllTracker(
          {
            pagination: this.pagination,
            sort,
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.userActivity') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      this.userActivity = (result && result.data) || [];
      this.setMetaDataFromResult(result);
      this.setLoaded(loadingToken);
    },

    manipulateString(string) {
      if (!string) return '';
      string = string.toLowerCase().replace(/_/g, ' ');
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    moduleLabel(module) {
      if (module && this.$te(`userActivity.modules.${module}`)) {
        return this.$t(`userActivity.modules.${module}`);
      }
      return this.manipulateString(module);
    },

    moduleColor(module) {
      const meta = MODULE_META[module];
      return meta ? meta.color : '#b4b7bd';
    },

    moduleIcon(module) {
      const meta = MODULE_META[module];
      return meta ? meta.icon : 'fas fa-cube';
    },

    actionMeta(action) {
      if (!action) return { variant: 'neutral', icon: 'fas fa-question', label: '—' };
      const key = action.toUpperCase();
      // i18n label: normalize "LOGIN-FAILED" / "RENEW PASSWORD" → LOGIN_FAILED / RENEW_PASSWORD
      const norm = key.replace(/[^A-Z0-9]+/g, '_');
      let label = null;
      if (this.$te(`userActivity.actions.${norm}`)) label = this.$t(`userActivity.actions.${norm}`);
      else if (norm.startsWith('GET_ALL')) label = this.$t('userActivity.actions.GET_ALL');
      else if (norm.startsWith('FETCH')) label = this.$t('userActivity.actions.FETCH');

      if (ACTION_META[key]) return { ...ACTION_META[key], label: label || ACTION_META[key].label };
      if (key.startsWith('GET ALL')) return { variant: 'neutral', icon: 'fas fa-list', label: label || 'Get all' };
      if (key.startsWith('FETCH')) return { variant: 'neutral', icon: 'fas fa-search', label: label || 'Fetch' };
      return { variant: 'neutral', icon: 'fas fa-bolt', label: label || this.manipulateString(action) };
    },

    actionVariant(action) {
      return this.actionMeta(action).variant;
    },

    actionIcon(action) {
      return this.actionMeta(action).icon;
    },

    actionLabel(action) {
      return this.actionMeta(action).label;
    },

    formatDate(value) {
      if (!value) return '';
      return this.$moment(value).format(this.$t('timeFormat.date.format'));
    },

    formatTime(value) {
      if (!value) return '';
      return this.$moment(value).format('HH:mm:ss');
    },

    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    avatarColor(name) {
      const palette = ['#007BFF', '#28c76f', '#00cfe8', '#ff9f43', '#ea5455', '#6e6b7b', '#ff6f91', '#3fbff7'];
      if (!name) return palette[0];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
      }
      return palette[hash % palette.length];
    },
  }
};
</script>

<style lang="scss" scoped>
.activity-table-wrapper {
  padding-bottom: 10px;
}

::v-deep .activity-table {
  margin-bottom: 0;

  thead th {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #b4b7bd;
    background: rgba(0, 123, 255, 0.04);
    border-bottom: 2px solid rgba(0, 123, 255, 0.12);
    border-top: 0;
    padding: 0.9rem 1rem;
    font-weight: 600;
  }

  tbody td {
    vertical-align: middle;
    padding: 0.7rem 1rem;
    border-top: 1px solid rgba(110, 117, 159, 0.08);
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 123, 255, 0.035) !important;
    }
  }

  .activity-td--registrationTime { width: 140px; }
  .activity-td--module            { width: 180px; }
  .activity-td--action            { width: 170px; }
  .activity-td--executor          { width: 220px; }
}

.activity-cell-time {
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  &__date {
    font-weight: 600;
    color: #5e5873;
    font-size: 0.82rem;
  }

  &__hour {
    font-size: 0.72rem;
    color: #8a91ad;
    font-variant-numeric: tabular-nums;
  }
}

.activity-cell-user {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 0.72rem;
    flex-shrink: 0;
    letter-spacing: 0.03em;
    box-shadow: 0 2px 6px -2px rgba(0, 0, 0, 0.2);
  }

  &__info {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    color: #5e5873;
    font-size: 0.82rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
  }

  &__type {
    font-size: 0.65rem;
    color: #8a91ad;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.activity-module-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  box-shadow: 0 2px 6px -3px rgba(0, 0, 0, 0.25);
}

.activity-action-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid transparent;

  &--success { background: rgba(40, 199, 111, 0.12); color: #0fa17a; border-color: rgba(40, 199, 111, 0.25); }
  &--info    { background: rgba(0, 123, 255, 0.12); color: #007BFF; border-color: rgba(0, 123, 255, 0.25); }
  &--warning { background: rgba(255, 159, 67, 0.15); color: #d9841f; border-color: rgba(255, 159, 67, 0.28); }
  &--danger  { background: rgba(234, 84, 85, 0.12);  color: #c53e3e; border-color: rgba(234, 84, 85, 0.25); }
  &--neutral { background: rgba(110, 117, 159, 0.1); color: #b4b7bd; border-color: rgba(110, 117, 159, 0.22); }
}

.activity-cell-description {
  max-width: 460px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #4b5268;
  font-size: 0.82rem;
}

.activity-empty {
  padding: 2.2rem 1rem;
  text-align: center;
  color: #8a91ad;

  &__icon {
    font-size: 2.2rem;
    color: #c7cbe0;
    margin-bottom: 0.6rem;
  }

  &__title {
    font-weight: 600;
    color: #4b5268;
    font-size: 0.95rem;
  }

  &__hint {
    font-size: 0.78rem;
    margin-top: 0.2rem;
  }
}

.activity-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0.25rem 0;
  flex-wrap: wrap;

  &__pager { flex: 1; display: flex; justify-content: center; }

  &__size {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.78rem;
    color: #b4b7bd;
  }

  &__summary {
    font-variant-numeric: tabular-nums;
  }
}

// Dark mode
::v-deep .dark-layout .activity-table {
  thead th {
    background: rgba(77, 163, 255, 0.07);
    border-bottom-color: rgba(77, 163, 255, 0.18);
    color: #b4b7bd;
  }

  tbody td { border-top-color: rgba(255, 255, 255, 0.06); }

  tbody tr:hover { background: rgba(77, 163, 255, 0.08) !important; }
}

.dark-layout {
  .activity-cell-time__date { color: #d0d2d6; }
  .activity-cell-time__hour { color: #a0a0a0; }
  .activity-cell-user__name { color: #d0d2d6; }
  .activity-cell-user__type { color: #a0a0a0; }
  .activity-cell-description { color: #b4b7bd; }
  .activity-empty__title { color: #d0d2d6; }
  .activity-empty__hint  { color: #a0a0a0; }

  .activity-action-chip {
    &--success { background: rgba(40, 199, 111, 0.18); color: #35d5a4; }
    &--info    { background: rgba(77, 163, 255, 0.18); color: #8fa7ff; }
    &--warning { background: rgba(255, 159, 67, 0.22); color: #ffb958; }
    &--danger  { background: rgba(234, 84, 85, 0.18);  color: #ff8080; }
    &--neutral { background: rgba(255, 255, 255, 0.08); color: #b4b7bd; border-color: rgba(255, 255, 255, 0.12); }
  }
}
</style>
